"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useSession } from "next-auth/react";
import {
    AnimatePresence,
    motion,
    useInView,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
} from "motion/react";
import Logo from "@/components/ui/Logo/Logo";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import AcceleratorLeadModal from "../AcceleratorLeadModal/AcceleratorLeadModal";
import TrainingFactSheet from "../TrainingFactSheet/TrainingFactSheet";
import AcceleratorSectionNav from "../AcceleratorSectionNav/AcceleratorSectionNav";
import { trainingFactValues } from "@/lib/trainingFacts";

// La page de l'accélérateur Python, refaite le 29/09/2026 dans la direction
// artistique pastel : fonds lavande, bleu ciel, menthe et beurre, BeBot mis
// en situation avec le petit python, un seul accent orange. Elle parle aux
// indépendants qui veulent automatiser leur travail, et le programme complet
// se télécharge.
//
// Le PDF est public/static/programmes/programme-formation-python.pdf. Il se
// régénère depuis l'administration (accélérateur Python, onglet
// « Programme PDF ») : après toute modification du programme ou de la
// fiche, remplacer ce fichier.

const PROGRAMME_PDF = "/static/programmes/programme-formation-python.pdf";
const IMG = (fichier) => `/images/programmes/python/${fichier}`;
const EASE = [0.22, 1, 0.36, 1];

// Les neuf modules du tableau de correspondance V2 (100 heures, 25 quiz).
const MODULES = [
    { hours: 7, quiz: 2 },
    { hours: 4, quiz: 2 },
    { hours: 7, quiz: 3 },
    { hours: 8, quiz: 1 },
    { hours: 7, quiz: 2 },
    { hours: 23, quiz: 1 },
    { hours: 7, quiz: 2 },
    { hours: 30, quiz: 5 },
    { hours: 7, quiz: 7 },
];

// Les onze projets : durée estimée en minutes, projet guidé ou non, module.
const PROJECTS = [
    { minutes: 90, guided: false, module: 4 },
    { minutes: 240, guided: false, module: 4 },
    { minutes: 300, guided: true, module: 5 },
    { minutes: 90, guided: false, module: 6 },
    { minutes: 240, guided: true, module: 6 },
    { minutes: 300, guided: true, module: 6 },
    { minutes: 300, guided: false, module: 6 },
    { minutes: 300, guided: true, module: 6 },
    { minutes: 300, guided: false, module: 8 },
    { minutes: 300, guided: false, module: 8 },
    { minutes: 300, guided: true, module: 8 },
];

const USES = [
    { key: "emails" },
    { key: "alerts" },
    { key: "reports" },
    { key: "publish" },
    { key: "apps" },
    { key: "web" },
];

const SUMMARY = [
    { key: "summary_hours", icon: "clock-circle-linear" },
    { key: "summary_projects", icon: "code-square-bold-duotone" },
    { key: "summary_quiz", icon: "checklist-minimalistic-bold-duotone" },
    { key: "summary_follow", icon: "videocamera-record-bold-duotone" },
    { key: "summary_certification", icon: "diploma-verified-bold-duotone" },
    { key: "summary_access", icon: "infinite-bold" },
];

const FAQ = [
    "code",
    "time",
    "use",
    "official",
    "mock",
    "included",
    "hard",
    "identity",
    "funding",
    "change",
    "access",
];

// La frise du parcours, de la candidature à l'après-formation.
const TIMELINE = [
    { phase: "apply", steps: ["apply", "call", "contract", "positioning"] },
    { phase: "training", steps: ["start", "weeks"] },
    { phase: "certification", steps: ["mock", "check", "exam"] },
    { phase: "after", steps: ["after"] },
];
const TIMELINE_ICONS = {
    apply: "document-text-bold-duotone",
    call: "videocamera-record-bold-duotone",
    contract: "shield-check-bold-duotone",
    positioning: "routing-2-bold-duotone",
    start: "calendar-bold-duotone",
    weeks: "code-square-bold-duotone",
    mock: "checklist-minimalistic-bold-duotone",
    check: "monitor-bold-duotone",
    exam: "diploma-verified-bold-duotone",
    after: "infinite-bold",
};
const TIMELINE_WEEKS = ["modules", "projects", "quiz", "follow"];
const RECAP = ["apply", "training", "certification", "after"];

function duree(minutes, locale) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (locale === "fr") return m ? `${h} h ${m}` : `${h} h`;
    return m ? `${h}h${m}` : `${h}h`;
}

function FadeIn({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.35, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// Petite étiquette de section : carré orange et capitales espacées, comme
// dans le programme PDF.
function Eyebrow({ children, dark = false }) {
    return (
        <p
            className={`mb-0 flex items-center gap-2.5 font-geistMono text-[11px] uppercase tracking-[0.18em] ${
                dark ? "text-[#8FB4E8]" : "text-primary"
            }`}
        >
            <span className="h-1.5 w-1.5 bg-primary" />
            {children}
        </p>
    );
}

function DownloadLink({ t, dark = false, className = "" }) {
    return (
        <a
            href={PROGRAMME_PDF}
            target="_blank"
            rel="noopener"
            className={`group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full border px-5 py-3 text-sm font-semibold transition-colors ${
                dark
                    ? "border-white/20 text-white hover:border-white/40 hover:bg-white/5"
                    : "border-slate-300 text-slate-900 hover:border-slate-400 hover:bg-slate-50"
            } ${className}`}
        >
            <SolarIcon
                name="file-download-bold-duotone"
                className={`text-lg ${dark ? "text-[#8FB4E8]" : "text-primary"}`}
            />
            {t("download")}
            <span
                className={`hidden text-xs font-normal sm:inline ${
                    dark ? "text-white/50" : "text-slate-500"
                }`}
            >
                {t("download_meta")}
            </span>
        </a>
    );
}

function Price({ t, dark = false, center = false }) {
    return (
        <div className={`flex flex-col gap-1 ${center ? "items-center" : ""}`}>
            <div className="flex items-baseline gap-2">
                <span
                    className={`font-geistSans text-4xl font-bold tracking-tight ${
                        dark ? "text-white" : "text-slate-900"
                    }`}
                >
                    {t("price_monthly")}
                </span>
                <span
                    className={`text-base font-medium ${
                        dark ? "text-white/80" : "text-slate-700"
                    }`}
                >
                    {t("price_monthly_suffix")}
                </span>
            </div>
            <span className={`text-xs ${dark ? "text-white/55" : "text-slate-500"}`}>
                {t("price_total")}
            </span>
        </div>
    );
}

// Le hero : BeBot en grand sur un fond pastel, entouré des tâches que Python
// abat pour vous. Chaque objet flotte à sa profondeur et suit la souris,
// plus ou moins selon sa distance : l'effet de relief vient de là.
const HERO_ITEMS = [
    { src: "enveloppe", x: 2, y: 14, w: 22, depth: 1.4, rot: -10, float: 4.6 },
    { src: "graphique", x: 74, y: 6, w: 23, depth: 1.1, rot: 8, float: 5.2 },
    { src: "tableur", x: 0, y: 56, w: 21, depth: 0.8, rot: -6, float: 5.8 },
    { src: "dossier", x: 80, y: 44, w: 19, depth: 1.6, rot: 6, float: 4.2 },
    { src: "calendrier", x: 16, y: 84, w: 15, depth: 1.2, rot: -8, float: 5 },
    { src: "engrenage", x: 60, y: 1, w: 12, depth: 0.6, rot: 0, float: 6.4, spin: true },
    { src: "avion", x: 20, y: -2, w: 17, depth: 1.8, rot: -4, float: 3.8 },
    { src: "code", x: 76, y: 80, w: 20, depth: 1, rot: 5, float: 5.6 },
];

const HERO_CHIPS = [
    { key: "hero_chip_followups", x: -4, y: 38, depth: 1.3, delay: 0.5 },
    { key: "hero_chip_report", x: 40, y: 90, depth: 1.5, delay: 0.8 },
];

function useParallax(mx, my, depth, amplitude = 26) {
    const x = useSpring(useTransform(mx, (v) => v * depth * amplitude), {
        stiffness: 120,
        damping: 18,
        mass: 0.4,
    });
    const y = useSpring(useTransform(my, (v) => v * depth * amplitude), {
        stiffness: 120,
        damping: 18,
        mass: 0.4,
    });
    return { x, y };
}

function HeroItem({ item, mx, my, reduce, index }) {
    const { x, y } = useParallax(mx, my, item.depth);
    return (
        <motion.div
            aria-hidden
            className={`absolute ${index > 5 ? "hidden sm:block" : ""}`}
            style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.w}%`, x, y }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.25 + index * 0.05, ease: EASE }}
        >
            <motion.div
                className="cursor-grab"
                style={{ rotate: item.rot }}
                animate={
                    reduce
                        ? undefined
                        : item.spin
                          ? { rotate: [0, 360] }
                          : { y: [0, -10, 0] }
                }
                transition={
                    item.spin
                        ? { duration: 24, repeat: Infinity, ease: "linear" }
                        : { duration: item.float, repeat: Infinity, ease: "easeInOut" }
                }
                whileHover={reduce ? undefined : { scale: 1.12, rotate: item.rot + 6 }}
            >
                <Image
                    src={IMG(`hero/${item.src}.webp`)}
                    alt=""
                    width={240}
                    height={240}
                    sizes="140px"
                    className="h-auto w-full select-none drop-shadow-[0_14px_18px_rgba(76,52,170,0.14)]"
                    draggable={false}
                />
            </motion.div>
        </motion.div>
    );
}

function HeroChip({ chip, t, mx, my, reduce }) {
    const { x, y } = useParallax(mx, my, chip.depth);
    return (
        <motion.div
            aria-hidden
            className="absolute hidden sm:block"
            style={{ left: `${chip.x}%`, top: `${chip.y}%`, x, y }}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, delay: chip.delay, ease: EASE }}
        >
            <motion.div
                animate={reduce ? undefined : { y: [0, -6, 0] }}
                transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: chip.delay }}
                className="flex items-center gap-2 whitespace-nowrap rounded-2xl bg-white/90 px-3.5 py-2.5 text-[13px] font-semibold text-slate-800 shadow-[0_12px_30px_-10px_rgba(76,52,170,0.35)] ring-1 ring-slate-900/5 backdrop-blur"
            >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                    <SolarIcon name="check-read-linear" className="text-sm text-emerald-600" />
                </span>
                {t(chip.key)}
            </motion.div>
        </motion.div>
    );
}

function HeroStage({ t, mx, my, reduce }) {
    const bebot = useParallax(mx, my, 0.45);
    const blob = useParallax(mx, my, -0.35);
    const tilt = useSpring(useTransform(mx, (v) => v * 6), { stiffness: 90, damping: 16 });
    return (
        <div className="relative mx-auto aspect-[1/1] w-full max-w-[540px]">
            {/* La forme douce derrière BeBot, comme dans les illustrations. */}
            <motion.div
                aria-hidden
                className="absolute inset-[9%] rounded-[42%_58%_46%_54%/52%_44%_56%_48%] bg-gradient-to-br from-[#DCE9FF] via-[#E6DEFF] to-[#DDF6EA]"
                style={{ x: blob.x, y: blob.y }}
                animate={reduce ? undefined : { rotate: [0, 6, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <div
                aria-hidden
                className="absolute left-[12%] top-[70%] h-[14%] w-[14%] rounded-full bg-[#FFE9B8]"
            />
            <div
                aria-hidden
                className="absolute right-[10%] top-[18%] h-[9%] w-[9%] rounded-full bg-[#FFD9CC]"
            />

            <motion.div
                className="absolute left-[21%] top-[12%] w-[58%]"
                style={{ x: bebot.x, y: bebot.y, rotate: tilt }}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: EASE }}
            >
                <motion.div
                    animate={reduce ? undefined : { y: [0, -12, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                    <Image
                        src={IMG("hero/bebot.webp")}
                        alt={t("hero_image_alt")}
                        width={900}
                        height={1127}
                        priority
                        sizes="(min-width: 1024px) 320px, 58vw"
                        className="h-auto w-full select-none"
                        draggable={false}
                    />
                </motion.div>
            </motion.div>

            {HERO_ITEMS.map((item, i) => (
                <HeroItem key={item.src} item={item} index={i} mx={mx} my={my} reduce={reduce} />
            ))}
            {HERO_CHIPS.map((chip) => (
                <HeroChip key={chip.key} chip={chip} t={t} mx={mx} my={my} reduce={reduce} />
            ))}
        </div>
    );
}

function Hero({ t, onApply }) {
    const facts = ["hours", "projects", "weeks", "certification"];
    const reduce = useReducedMotion();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);

    const onMove = (event) => {
        if (reduce || event.pointerType !== "mouse") return;
        const rect = event.currentTarget.getBoundingClientRect();
        mx.set((event.clientX - rect.left) / rect.width - 0.5);
        my.set((event.clientY - rect.top) / rect.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            className="relative overflow-hidden bg-[#F8F6FF]"
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            {/* Taches de couleur pastel, floues, sans quadrillage. */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#E4DCFF] blur-3xl" />
                <div className="absolute -right-32 top-10 h-[480px] w-[480px] rounded-full bg-[#D8ECFF] blur-3xl" />
                <div className="absolute bottom-[-220px] left-1/3 h-[460px] w-[460px] rounded-full bg-[#DDF6EA] blur-3xl" />
                <div className="absolute right-1/4 top-2/3 h-[240px] w-[240px] rounded-full bg-[#FFF1C9] blur-3xl" />
            </div>

            <div className="relative mx-auto grid max-w-6xl items-center gap-6 px-6 pb-12 pt-10 sm:pt-14 lg:grid-cols-[1.02fr_1fr] lg:gap-4 lg:pb-16 lg:pt-16">
                <div className="order-2 lg:order-1">
                    <FadeIn>
                        <Eyebrow>{t("eyebrow")}</Eyebrow>
                    </FadeIn>
                    <FadeIn delay={0.04}>
                        <h1 className="mt-6 font-geistSans text-[2.6rem] font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl">
                            {t("title_line1")}
                            <br />
                            <span className="text-primary">{t("title_line2")}</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.08}>
                        <p className="mb-0 mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                            {t("subtitle")}
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <div className="mt-9 flex flex-wrap items-end gap-x-8 gap-y-6">
                            <Price t={t} />
                            <div className="flex flex-col gap-2">
                                <Button
                                    type="primary"
                                    onClick={onApply}
                                    className="w-full !rounded-full text-base font-semibold sm:w-auto"
                                >
                                    <span className="inline-flex items-center gap-2">
                                        {t("cta_primary")}
                                        <SolarIcon name="arrow-right-linear" className="text-lg" />
                                    </span>
                                </Button>
                                <span className="text-xs text-slate-500">
                                    {t("cta_benefit")}
                                </span>
                            </div>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.16}>
                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                            <DownloadLink t={t} className="bg-white/70 backdrop-blur" />
                            <a
                                href="#cpf"
                                className="inline-flex items-center gap-1.5 text-sm text-slate-500 underline-offset-4 transition-colors hover:text-slate-900 hover:underline"
                            >
                                {t("hero_cpf_link")}
                                <SolarIcon name="arrow-right-linear" className="text-base" />
                            </a>
                        </div>
                    </FadeIn>
                </div>

                <div className="order-1 lg:order-2">
                    <HeroStage t={t} mx={mx} my={my} reduce={reduce} />
                </div>
            </div>

            {/* Les quatre repères, sur une bande blanche translucide. */}
            <div className="relative mx-auto max-w-6xl px-6 pb-12 lg:pb-16">
                <div className="grid grid-cols-2 overflow-hidden rounded-3xl bg-white/75 ring-1 ring-slate-900/5 backdrop-blur sm:grid-cols-4">
                    {facts.map((fact, i) => (
                        <div
                            key={fact}
                            className={`px-6 py-6 sm:px-8 ${
                                i > 0 ? "sm:border-l sm:border-slate-900/5" : ""
                            } ${i % 2 === 1 ? "border-l border-slate-900/5" : ""} ${
                                i > 1 ? "border-t border-slate-900/5 sm:border-t-0" : ""
                            }`}
                        >
                            <p className="mb-0 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                                {t(`fact_${fact}_value`)}
                            </p>
                            <p className="mb-0 mt-1.5 text-sm text-slate-500">
                                {t(`fact_${fact}_label`)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// La réponse à « qu'est-ce que c'est ? » : BeBot en chef d'orchestre des
// automatisations (Python travaille, vous dirigez), une réponse courte qui se
// cite telle quelle, puis trois repères illustrés.
const BRIEF = [
    { key: "who", icon: "icone-pour-qui.png" },
    { key: "how", icon: "icone-comment.png" },
    { key: "end", icon: "icone-a-la-fin.png" },
];

function Brief({ t }) {
    return (
        <section id="en-bref" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
                <FadeIn>
                    <div className="relative mx-auto aspect-[4/3] w-full max-w-xl">
                        <Image
                            src={IMG("bebot-chef-orchestre.jpg")}
                            alt={t("brief_image_alt")}
                            fill
                            sizes="(min-width: 1024px) 540px, 100vw"
                            className="object-contain"
                        />
                    </div>
                </FadeIn>

                <FadeIn delay={0.05}>
                    <Eyebrow>{t("brief_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.4rem] sm:leading-[1.1]">
                        {t("brief_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t.rich("brief_answer", {
                            strong: (chunks) => (
                                <span className="font-semibold text-slate-900">{chunks}</span>
                            ),
                        })}
                    </p>

                    <ul className="mb-0 mt-8 space-y-5 pl-0">
                        {BRIEF.map((item) => (
                            <li key={item.key} className="flex list-none items-start gap-4">
                                <span className="relative h-14 w-14 shrink-0 rounded-2xl bg-slate-50">
                                    <Image
                                        src={IMG(item.icon)}
                                        alt=""
                                        fill
                                        sizes="56px"
                                        className="object-contain p-1.5"
                                    />
                                </span>
                                <div>
                                    <h3 className="mb-0 text-base font-semibold text-slate-900">
                                        {t(`brief_${item.key}_title`)}
                                    </h3>
                                    <p className="mb-0 mt-1 text-[15px] leading-relaxed text-slate-600">
                                        {t(`brief_${item.key}_text`)}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <p className="mb-0 mt-8 flex items-center gap-2.5 text-sm text-slate-500">
                        <SolarIcon name="shield-check-bold-duotone" className="text-lg text-[#613BDB]" />
                        {t("brief_org")}
                    </p>
                </FadeIn>
            </div>
        </section>
    );
}

// Un extrait de script par cas d'usage, pour montrer à quoi ressemble le
// travail confié à Python. Les noms suivent la langue de la page.
const USE_CODE = {
    fr: {
        emails: [
            "import smtplib, datetime",
            "",
            "if datetime.date.today().weekday() == 0:",
            "    for client in clients_en_retard():",
            "        envoyer_relance(client)",
        ],
        alerts: [
            "prix = requests.get(URL).json()[\"prix\"]",
            "",
            "if prix < seuil:",
            "    sms.envoyer(f\"Le prix est passé à {prix} €\")",
        ],
        reports: [
            "import pandas as pd",
            "",
            "ventes = pd.read_csv(\"ventes.csv\")",
            "par_mois = ventes.groupby(\"mois\")[\"montant\"].sum()",
            "par_mois.to_csv(\"rapport.csv\")",
        ],
        publish: [
            "navigateur.get(\"https://x.com\")",
            "",
            "texte = publication_du_jour()",
            "publier(navigateur, texte)",
        ],
        apps: [
            "import tkinter as tk",
            "",
            "fenetre = tk.Tk()",
            "tk.Button(fenetre, text=\"Calculer\", command=calculer).pack()",
            "fenetre.mainloop()",
        ],
        web: [
            "page = requests.get(URL)",
            "soupe = BeautifulSoup(page.text, \"html.parser\")",
            "",
            "titres = [h2.text for h2 in soupe.select(\"h2\")]",
        ],
    },
    en: {
        emails: [
            "import smtplib, datetime",
            "",
            "if datetime.date.today().weekday() == 0:",
            "    for client in overdue_clients():",
            "        send_reminder(client)",
        ],
        alerts: [
            "price = requests.get(URL).json()[\"price\"]",
            "",
            "if price < threshold:",
            "    sms.send(f\"The price dropped to {price}\")",
        ],
        reports: [
            "import pandas as pd",
            "",
            "sales = pd.read_csv(\"sales.csv\")",
            "by_month = sales.groupby(\"month\")[\"amount\"].sum()",
            "by_month.to_csv(\"report.csv\")",
        ],
        publish: [
            "browser.get(\"https://x.com\")",
            "",
            "text = todays_post()",
            "publish(browser, text)",
        ],
        apps: [
            "import tkinter as tk",
            "",
            "window = tk.Tk()",
            "tk.Button(window, text=\"Calculate\", command=calculate).pack()",
            "window.mainloop()",
        ],
        web: [
            "page = requests.get(URL)",
            "soup = BeautifulSoup(page.text, \"html.parser\")",
            "",
            "titles = [h2.text for h2 in soup.select(\"h2\")]",
        ],
    },
};

// Coloration minimale : mots-clés et chaînes, sans librairie.
function CodeLine({ line }) {
    if (!line) return <span>&nbsp;</span>;
    const parts = [];
    const regex = /(f?"[^"]*")|\b(import|from|as|if|for|in|def|return|and|or|not)\b/g;
    let last = 0;
    let match;
    while ((match = regex.exec(line))) {
        if (match.index > last) parts.push(<span key={last}>{line.slice(last, match.index)}</span>);
        parts.push(
            <span
                key={match.index}
                className={match[1] ? "text-[#F4A27F]" : "text-[#8FB4E8]"}
            >
                {match[0]}
            </span>,
        );
        last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(<span key={last}>{line.slice(last)}</span>);
    return parts;
}

function Uses({ t }) {
    const locale = useLocale();
    const [active, setActive] = useState(0);
    const use = USES[active];
    const code = (USE_CODE[locale] || USE_CODE.fr)[use.key];

    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <FadeIn className="max-w-2xl">
                    <Eyebrow>{t("uses_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("uses_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("uses_subtitle")}
                    </p>
                </FadeIn>

                <div className="mt-14 grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-10">
                    {/* Les six cas : des onglets, au clavier comme à la souris. */}
                    <div
                        role="tablist"
                        aria-label={t("uses_eyebrow")}
                        className="-mx-6 flex scroll-px-6 gap-2 overflow-x-auto px-6 pb-4 pt-1 [scrollbar-width:none] lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0 lg:pt-0 [&::-webkit-scrollbar]:hidden"
                    >
                        {USES.map((item, i) => {
                            const selected = i === active;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="tab"
                                    id={`use-tab-${item.key}`}
                                    aria-selected={selected}
                                    aria-controls="use-panel"
                                    onClick={() => setActive(i)}
                                    className={`group flex shrink-0 cursor-pointer items-center gap-4 rounded-2xl border px-4 py-3 text-left text-slate-900 transition-colors lg:px-5 lg:py-3.5 ${
                                        selected
                                            ? "border-[#613BDB]/30 bg-[#F1ECFF] shadow-[0_10px_24px_-14px_rgba(97,59,219,0.5)]"
                                            : "border-slate-200 bg-white hover:border-[#613BDB]/20 hover:bg-[#FAF8FF]"
                                    }`}
                                >
                                    <span
                                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors ${
                                            selected ? "bg-white" : "bg-[#F6F3FF]"
                                        }`}
                                    >
                                        <Image
                                            src={IMG(`hero/usage-${item.key}.webp`)}
                                            alt=""
                                            width={160}
                                            height={160}
                                            sizes="36px"
                                            className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
                                        />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block whitespace-nowrap font-semibold lg:whitespace-normal">
                                            {t(`use_${item.key}_title`)}
                                        </span>
                                        <span
                                            className={`mt-0.5 hidden font-geistMono text-[11px] uppercase tracking-[0.12em] lg:block ${
                                                selected ? "text-[#613BDB]/70" : "text-slate-400"
                                            }`}
                                        >
                                            {t(`use_${item.key}_where`)}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Le cas choisi : la corvée d'avant, ce que fait le script. */}
                    <div
                        id="use-panel"
                        role="tabpanel"
                        aria-labelledby={`use-tab-${use.key}`}
                        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#EAF1FF] via-[#F1ECFF] to-[#E6F7EF] text-slate-900 ring-1 ring-slate-900/5"
                    >
                        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/50 blur-2xl" />
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                                key={use.key}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.22, ease: EASE }}
                                className="relative flex h-full flex-col p-6 sm:p-8"
                            >
                                <Image
                                    src={IMG(`hero/usage-${use.key}.webp`)}
                                    alt=""
                                    width={160}
                                    height={160}
                                    sizes="88px"
                                    className="absolute bottom-4 right-5 z-10 hidden h-16 w-16 rotate-6 object-contain drop-shadow-[0_12px_16px_rgba(76,52,170,0.22)] sm:block"
                                />
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-2xl bg-white/70 p-5 ring-1 ring-slate-900/5">
                                        <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                                            <SolarIcon name="history-bold-duotone" className="text-sm" />
                                            {t("uses_before")}
                                        </p>
                                        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-600">
                                            {t(`use_${use.key}_before`)}
                                        </p>
                                    </div>
                                    <div className="rounded-2xl bg-white p-5 ring-1 ring-primary/25 shadow-[0_12px_28px_-18px_rgba(232,81,29,0.55)]">
                                        <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-primary">
                                            <SolarIcon name="refresh-circle-bold-duotone" className="text-sm" />
                                            {t("uses_after")}
                                        </p>
                                        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-900">
                                            {t(`use_${use.key}_after`)}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 flex-1 overflow-hidden rounded-2xl bg-[#1C1936] shadow-[0_18px_40px_-20px_rgba(28,25,54,0.6)]">
                                    <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-2.5">
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FF8A80]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FFD76E]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#7BE0A8]" />
                                        <span className="ml-3 font-geistMono text-xs text-white/40">
                                            {t(`use_${use.key}_file`)}
                                        </span>
                                    </div>
                                    <pre className="m-0 overflow-x-auto bg-transparent p-4 font-geistMono text-[13px] leading-6 text-white/85">
                                        <code>
                                            {code.map((line, i) => (
                                                <div key={i}>
                                                    <CodeLine line={line} />
                                                </div>
                                            ))}
                                        </code>
                                    </pre>
                                </div>

                                <p className="mb-0 mt-4 flex items-center gap-2 text-sm text-slate-500">
                                    <SolarIcon name="routing-2-bold-duotone" className="text-base text-[#613BDB]" />
                                    {t("uses_learned_in", { where: t(`use_${use.key}_where`) })}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SummaryCard({ t, onApply }) {
    return (
        <div className="overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-28px_rgba(76,52,170,0.45)] ring-1 ring-slate-900/5">
            <div className="relative h-36 overflow-hidden bg-gradient-to-br from-[#DCE9FF] via-[#E9E2FF] to-[#DDF6EA]">
                <div aria-hidden className="absolute -left-6 bottom-[-30px] h-24 w-24 rounded-full bg-[#FFF1C9]" />
                <Image
                    src={IMG("hero/bebot.webp")}
                    alt=""
                    width={900}
                    height={1127}
                    sizes="120px"
                    className="absolute bottom-[-18px] left-1/2 h-[150px] w-auto -translate-x-1/2"
                />
                <Image
                    src={IMG("hero/graphique.webp")}
                    alt=""
                    width={240}
                    height={200}
                    sizes="60px"
                    className="absolute right-5 top-4 h-auto w-14 rotate-6"
                />
                <Image
                    src={IMG("hero/enveloppe.webp")}
                    alt=""
                    width={240}
                    height={190}
                    sizes="60px"
                    className="absolute left-6 top-6 h-auto w-12 -rotate-12"
                />
            </div>
            <div className="p-6">
                <p className="mb-0 font-geistMono text-[11px] uppercase tracking-[0.18em] text-[#613BDB]">
                    {t("summary_eyebrow")}
                </p>
                <div className="mt-4">
                    <Price t={t} />
                </div>
                <ul className="mb-0 mt-6 space-y-3 border-t border-slate-100 pt-5">
                    {SUMMARY.map((row) => (
                        <li key={row.key} className="flex items-start gap-3 text-sm text-slate-700">
                            <SolarIcon name={row.icon} className="mt-0.5 shrink-0 text-base text-[#613BDB]" />
                            {t(row.key)}
                        </li>
                    ))}
                </ul>
                <Button type="primary" block onClick={onApply} className="mt-6 !rounded-full">
                    {t("cta_primary")}
                </Button>
                <a
                    href={PROGRAMME_PDF}
                    target="_blank"
                    rel="noopener"
                    className="mt-3 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:text-slate-900"
                >
                    <SolarIcon name="download-minimalistic-bold" className="text-base" />
                    {t("download")}
                </a>
            </div>
        </div>
    );
}

function Programme({ t, onApply }) {
    const [open, setOpen] = useState(0);
    return (
        <section id="programme" className="scroll-mt-32 border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <FadeIn className="max-w-2xl">
                    <Eyebrow>{t("programme_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("programme_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("programme_subtitle")}
                    </p>
                </FadeIn>

                <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1fr_360px]">
                    <ol className="mb-0 space-y-3 pl-0">
                        {MODULES.map((module, i) => {
                            const n = i + 1;
                            const isOpen = open === i;
                            return (
                                <li key={n} className="list-none">
                                    <div
                                        className={`overflow-hidden rounded-2xl border bg-white transition-colors ${
                                            isOpen ? "border-slate-300" : "border-slate-200"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setOpen(isOpen ? -1 : i)}
                                            aria-expanded={isOpen}
                                            className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left sm:gap-5 sm:px-5"
                                        >
                                            <div className="relative hidden h-14 w-24 shrink-0 overflow-hidden rounded-lg sm:block">
                                                <Image
                                                    src={IMG(`module-${String(n).padStart(2, "0")}.jpg`)}
                                                    alt=""
                                                    fill
                                                    sizes="96px"
                                                    className="object-cover"
                                                />
                                            </div>
                                            <span className="w-9 shrink-0 font-geistSans text-2xl font-bold text-primary">
                                                {String(n).padStart(2, "0")}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block font-semibold text-slate-900">
                                                    {t(`module_${n}_title`)}
                                                </span>
                                                <span className="mt-0.5 block text-sm text-slate-500">
                                                    {t("module_meta", {
                                                        hours: module.hours,
                                                        quiz: module.quiz,
                                                    })}
                                                </span>
                                            </span>
                                            <SolarIcon
                                                name="alt-arrow-down-linear"
                                                className={`shrink-0 text-xl text-slate-400 transition-transform duration-300 ${
                                                    isOpen ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.28, ease: EASE }}
                                                >
                                                    <div className="grid gap-5 border-t border-slate-100 px-5 pb-6 pt-5 sm:grid-cols-[1fr_220px]">
                                                        <div>
                                                            <p className="mb-0 text-[15px] leading-relaxed text-slate-600">
                                                                {t(`module_${n}_text`)}
                                                            </p>
                                                            <p className="mb-0 mt-4 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                                                                {t("module_practice")}
                                                            </p>
                                                            <p className="mb-0 mt-1.5 text-[15px] font-medium text-slate-800">
                                                                {t(`module_${n}_practice`)}
                                                            </p>
                                                        </div>
                                                        <div className="relative aspect-[16/9] overflow-hidden rounded-xl sm:aspect-auto">
                                                            <Image
                                                                src={IMG(`module-${String(n).padStart(2, "0")}.jpg`)}
                                                                alt=""
                                                                fill
                                                                sizes="220px"
                                                                className="object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    <div className="lg:sticky lg:top-24">
                        <SummaryCard t={t} onApply={onApply} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Projects({ t }) {
    const locale = useLocale();
    return (
        <section id="projets" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <FadeIn className="max-w-2xl">
                        <Eyebrow>{t("projects_eyebrow")}</Eyebrow>
                        <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                            {t("projects_title")}
                        </h2>
                        <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                            {t("projects_subtitle")}
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.05}>
                        <p className="mb-0 flex items-center gap-2 text-sm text-slate-500">
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {t("project_guided")}
                            </span>
                            {t("projects_guided_hint")}
                        </p>
                    </FadeIn>
                </div>

                <ol className="mb-0 mt-14 grid gap-5 pl-0 sm:grid-cols-2 lg:grid-cols-3">
                    {PROJECTS.map((project, i) => (
                        <FadeIn key={i} delay={(i % 3) * 0.05}>
                            <li className="group h-full list-none overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_18px_40px_-20px_rgba(11,16,32,0.35)]">
                                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                                    <Image
                                        src={IMG(`projet-${String(i + 1).padStart(2, "0")}.jpg`)}
                                        alt=""
                                        fill
                                        sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
                                        className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                                    />
                                    <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 font-geistSans text-sm font-bold text-slate-900 shadow-sm">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {project.guided && (
                                        <span className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-white">
                                            {t("project_guided")}
                                        </span>
                                    )}
                                </div>
                                <div className="p-5">
                                    <p className="mb-0 font-semibold text-slate-900">
                                        {t(`project_${i + 1}`)}
                                    </p>
                                    <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-600">
                                        {t(`project_${i + 1}_text`)}
                                    </p>
                                    <p className="mb-0 mt-3 flex items-center gap-2 text-sm text-slate-500">
                                        <SolarIcon name="clock-circle-linear" className="text-base text-slate-400" />
                                        {duree(project.minutes, locale)}
                                        <span aria-hidden>·</span>
                                        {t("project_module", { module: project.module })}
                                    </p>
                                </div>
                            </li>
                        </FadeIn>
                    ))}

                    {/* La douzième case : le programme complet. */}
                    <FadeIn delay={0.1}>
                        <li className="relative flex h-full list-none flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-[#E9E2FF] via-[#E6EEFF] to-[#DDF6EA] p-6 ring-1 ring-slate-900/5">
                            <Image
                                src={IMG("hero/calendrier.webp")}
                                alt=""
                                width={240}
                                height={260}
                                sizes="80px"
                                className="absolute -right-3 -top-3 h-auto w-20 rotate-12 opacity-90"
                            />
                            <div className="relative">
                                <SolarIcon name="document-text-bold-duotone" className="text-3xl text-[#613BDB]" />
                                <p className="mb-0 mt-4 font-geistSans text-xl font-bold leading-snug text-slate-900">
                                    {t("projects_more_title")}
                                </p>
                                <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-600">
                                    {t("projects_more_text")}
                                </p>
                            </div>
                            <DownloadLink t={t} className="relative mt-6 self-start bg-white/80" />
                        </li>
                    </FadeIn>
                </ol>
            </div>
        </section>
    );
}

function FollowUp({ t }) {
    const points = ["weekly", "call", "questions", "corrections", "months"];
    return (
        <section id="suivi" className="scroll-mt-32 border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <FadeIn>
                    <div className="relative aspect-[1/1] overflow-hidden rounded-3xl">
                        <Image
                            src={IMG("suivi-visio.jpg")}
                            alt={t("follow_image_alt")}
                            fill
                            sizes="(min-width: 1024px) 560px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
                <FadeIn delay={0.05}>
                    <Eyebrow>{t("follow_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("follow_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("follow_subtitle")}
                    </p>
                    <ul className="mb-0 mt-8 space-y-4 pl-0">
                        {points.map((point) => (
                            <li key={point} className="flex list-none items-start gap-3">
                                <SolarIcon name="check-circle-bold" className="mt-0.5 shrink-0 text-xl text-primary" />
                                <span className="text-[15px] leading-relaxed text-slate-700">
                                    <span className="font-semibold text-slate-900">
                                        {t(`follow_${point}_title`)}
                                    </span>{" "}
                                    {t(`follow_${point}_text`)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </FadeIn>
            </div>
        </section>
    );
}

function TimelineStep({ t, step, last }) {
    const ref = useRef(null);
    // Le point s'allume quand l'étape passe au tiers haut de l'écran, et reste
    // allumé : la frise se remplit au fil de la lecture.
    const reached = useInView(ref, { once: true, margin: "0px 0px -35% 0px" });
    const highlight = step === "exam";
    return (
        <li ref={ref} className={`relative list-none pl-14 ${last ? "" : "pb-10"}`}>
            <span
                className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-500 ${
                    reached
                        ? "border-primary bg-primary text-white"
                        : "border-slate-200 bg-white text-slate-400"
                }`}
            >
                <SolarIcon name={TIMELINE_ICONS[step]} className="text-lg" />
            </span>
            <p className="mb-0 pt-0.5 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                {t(`timeline_${step}_when`)}
            </p>
            <div
                className={
                    highlight
                        ? "mt-2 rounded-2xl border border-primary/30 bg-primary/[0.04] p-5"
                        : "mt-1"
                }
            >
                <h3 className="mb-0 text-lg font-semibold text-slate-900">
                    {t(`timeline_${step}_title`)}
                </h3>
                {step === "weeks" ? (
                    <ul className="mb-0 mt-3 grid gap-2 pl-0 sm:grid-cols-2">
                        {TIMELINE_WEEKS.map((item) => (
                            <li
                                key={item}
                                className="flex list-none items-start gap-2 text-[15px] text-slate-600"
                            >
                                <SolarIcon
                                    name="check-circle-bold"
                                    className="mt-0.5 shrink-0 text-base text-primary"
                                />
                                {t(`timeline_weeks_${item}`)}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mb-0 mt-1.5 text-[15px] leading-relaxed text-slate-600">
                        {t(`timeline_${step}_text`)}
                    </p>
                )}
            </div>
            {step === "exam" && (
                <div className="mt-3 flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                    <SolarIcon
                        name="history-bold-duotone"
                        className="mt-0.5 shrink-0 text-lg text-[#3D5A8A]"
                    />
                    <p className="mb-0 text-sm leading-relaxed text-slate-600">
                        {t("timeline_exam_note")}
                    </p>
                </div>
            )}
        </li>
    );
}

function Timeline({ t }) {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 65%", "end 65%"],
    });
    const fill = useSpring(scrollYProgress, {
        stiffness: 120,
        damping: 30,
        mass: 0.4,
    });
    const steps = TIMELINE.flatMap((phase) => phase.steps);
    const lastStep = steps[steps.length - 1];

    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-16">
                <div className="lg:sticky lg:top-28 lg:self-start">
                    <FadeIn>
                        <Eyebrow>{t("timeline_eyebrow")}</Eyebrow>
                        <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                            {t("timeline_title")}
                        </h2>
                        <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                            {t("timeline_subtitle")}
                        </p>
                    </FadeIn>
                    <div className="mt-10 grid grid-cols-2 gap-3">
                        {RECAP.map((item, i) => (
                            <div
                                key={item}
                                className={`rounded-2xl p-5 ${
                                    i === 1 ? "bg-[#F1ECFF] ring-1 ring-[#613BDB]/15" : "bg-slate-50"
                                }`}
                            >
                                <p
                                    className={`mb-0 font-geistMono text-[11px] uppercase tracking-[0.14em] ${
                                        i === 1 ? "text-[#613BDB]" : "text-slate-400"
                                    }`}
                                >
                                    {t(`timeline_recap_${item}_label`)}
                                </p>
                                <p className="mb-0 mt-2 font-geistSans text-2xl font-bold tracking-tight text-slate-900">
                                    {t(`timeline_recap_${item}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">
                                    {t(`timeline_recap_${item}_text`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div ref={ref} className="relative">
                    {/* Le rail de la frise, puis son remplissage au défilement. */}
                    <span
                        aria-hidden
                        className="absolute bottom-5 left-5 top-5 w-px -translate-x-1/2 bg-slate-200"
                    />
                    <motion.span
                        aria-hidden
                        className="absolute bottom-5 left-5 top-5 w-[2px] origin-top -translate-x-1/2 bg-primary"
                        style={{ scaleY: reduce ? 1 : fill }}
                    />
                    {TIMELINE.map((phase) => (
                        <div key={phase.phase}>
                            <p className="relative mb-6 ml-14 inline-flex rounded-full bg-[#613BDB] px-3 py-1 text-xs font-semibold text-white">
                                {t(`timeline_phase_${phase.phase}`)}
                            </p>
                            <ol className="mb-0 pl-0">
                                {phase.steps.map((step) => (
                                    <TimelineStep
                                        key={step}
                                        t={t}
                                        step={step}
                                        last={step === lastStep}
                                    />
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Les indicateurs de résultats que Qualiopi demande de publier (indicateurs 2
// et 3), en premier et en grand, puis les chiffres de notoriété, à part et
// plus discrets : ils ne mesurent pas la formation elle-même.
const MARKETING_STATS = ["youtube", "trustpilot", "members"];

function Stats({ t }) {
    return (
        <section id="resultats" className="scroll-mt-32 border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <FadeIn className="max-w-2xl">
                    <Eyebrow>{t("stats_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("stats_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("stats_subtitle")}
                    </p>
                </FadeIn>

                <div className="mt-12 grid gap-4 lg:grid-cols-[1.25fr_1fr]">
                    <FadeIn>
                        <div className="relative h-full overflow-hidden rounded-3xl bg-gradient-to-br from-[#E9E2FF] via-[#EEF2FF] to-[#FFF1E6] p-8 ring-1 ring-slate-900/5 sm:p-10">
                            <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/60 blur-2xl" />
                            <div className="relative">
                                <p className="mb-0 inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 font-geistMono text-[11px] uppercase tracking-[0.14em] text-[#613BDB]">
                                    <SolarIcon name="shield-check-bold-duotone" className="text-sm" />
                                    {t("stats_indicator_badge")}
                                </p>
                                <p className="mb-0 mt-8 font-geistSans text-2xl font-semibold text-slate-900">
                                    {t("stats_rate_label")}
                                </p>
                                <p className="mb-0 mt-3 font-geistSans text-5xl font-bold tracking-tight text-slate-400">
                                    {t("stats_rate_value")}
                                </p>
                                <p className="mb-0 mt-4 max-w-md text-sm leading-relaxed text-slate-600">
                                    {t("stats_rate_method")}
                                </p>
                            </div>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.05}>
                        <div className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
                            <p className="mb-0 inline-flex items-center gap-2 self-start rounded-full border border-slate-200 px-3 py-1 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-500">
                                <SolarIcon name="diploma-verified-bold-duotone" className="text-sm text-primary" />
                                {t("stats_indicator_badge")}
                            </p>
                            <p className="mb-0 mt-8 font-geistSans text-2xl font-semibold leading-snug text-slate-900">
                                {t("stats_pass_label")}
                            </p>
                            <p className="mb-0 mt-3 font-geistSans text-4xl font-bold tracking-tight text-slate-300">
                                {t("stats_pass_value")}
                            </p>
                            <p className="mb-0 mt-auto pt-6 text-sm leading-relaxed text-slate-500">
                                {t("stats_pass_text")}
                            </p>
                        </div>
                    </FadeIn>
                </div>

                <FadeIn delay={0.1}>
                    <div className="mt-10 grid gap-6 border-t border-slate-200 pt-8 sm:grid-cols-3">
                        {MARKETING_STATS.map((stat) => (
                            <div key={stat}>
                                <p className="mb-0 font-geistSans text-3xl font-bold tracking-tight text-slate-900">
                                    {t(`stats_${stat}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">
                                    {t(`stats_${stat}_label`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Trainer({ t }) {
    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
                <FadeIn className="lg:order-2">
                    <div className="relative mx-auto aspect-[4/5] max-w-md overflow-hidden rounded-3xl">
                        <Image
                            src={IMG("formateur.jpg")}
                            alt={t("trainer_name")}
                            fill
                            sizes="(min-width: 1024px) 460px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
                <FadeIn delay={0.05} className="lg:order-1">
                    <Eyebrow>{t("trainer_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("trainer_name")}
                    </h2>
                    <p className="mb-0 mt-2 font-geistMono text-xs uppercase tracking-[0.16em] text-slate-500">
                        {t("trainer_role")}
                    </p>
                    <p className="mb-0 mt-6 text-lg leading-relaxed text-slate-600">
                        {t("trainer_text_1")}
                    </p>
                    <p className="mb-0 mt-4 text-lg leading-relaxed text-slate-600">
                        {t("trainer_text_2")}
                    </p>
                    <div className="mt-8 grid grid-cols-2 gap-3">
                        {["youtube", "since"].map((stat, i) => (
                            <div
                                key={stat}
                                className={`rounded-2xl p-5 ${
                                    i === 0 ? "bg-[#F1ECFF] ring-1 ring-[#613BDB]/15" : "bg-slate-50"
                                }`}
                            >
                                <p
                                    className={`mb-0 font-geistSans text-3xl font-bold tracking-tight ${
                                        i === 0 ? "text-primary" : "text-slate-900"
                                    }`}
                                >
                                    {t(`trainer_${stat}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">
                                    {t(`trainer_${stat}_label`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Certification({ t }) {
    const figures = ["questions", "minutes", "points"];
    return (
        <section id="certification" className="scroll-mt-32 relative overflow-hidden bg-[#F2FBF6] px-6 py-20 sm:py-28">
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-[#DDF6EA] blur-3xl" />
                <div className="absolute -right-24 bottom-0 h-[380px] w-[380px] rounded-full bg-[#E4DCFF] blur-3xl" />
            </div>
            <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
                <FadeIn>
                    <Eyebrow>{t("certification_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("certification_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("certification_text")}
                    </p>
                    <div className="mt-10 grid gap-5 border-t border-slate-900/10 pt-8 sm:grid-cols-3 sm:gap-0">
                        {figures.map((figure, i) => (
                            <div
                                key={figure}
                                className={`flex items-baseline gap-4 sm:block ${i > 0 ? "border-t border-slate-900/10 pt-5 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0" : ""}`}
                            >
                                <p
                                    className={`mb-0 font-geistSans text-3xl font-bold tracking-tight sm:text-4xl ${
                                        i === 2 ? "text-primary" : "text-slate-900"
                                    }`}
                                >
                                    {t(`certification_${figure}_value`)}
                                </p>
                                <p className="mb-0 text-sm text-slate-500 sm:mt-1.5">
                                    {t(`certification_${figure}_label`)}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p className="mb-0 mt-8 text-sm leading-relaxed text-slate-500">
                        {t("certification_note")}
                    </p>
                </FadeIn>
                <FadeIn delay={0.05}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-[0_30px_60px_-30px_rgba(76,52,170,0.4)]">
                        <Image
                            src={IMG("certification.jpg")}
                            alt=""
                            fill
                            sizes="(min-width: 1024px) 560px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// La liste d'attente du financement CPF (ouvert début 2027). Elle passe par
// la route des listes d'attente des formations, qui dédoublonne et prévient
// l'équipe par e-mail ; le slug la range à part et « contexte » adapte
// l'alerte. Un membre s'inscrit d'un clic, un visiteur laisse son adresse,
// dont son navigateur garde le souvenir.
const CPF_SLUG = "cpf-accelerateur-python";
const CPF_STORAGE = `believemy_waitlist_${CPF_SLUG}`;

function CpfWaitlist({ t }) {
    const { data: session, status } = useSession();
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | joined | error

    useEffect(() => {
        if (status === "loading") return undefined;
        let cancelled = false;
        if (!session?.user) {
            try {
                if (window.localStorage.getItem(CPF_STORAGE)) setState("joined");
            } catch (error) {
                // Stockage indisponible : on propose simplement l'inscription.
            }
            return undefined;
        }
        (async () => {
            try {
                const response = await fetch("/api/course-waitlist/status", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ courseSlug: CPF_SLUG }),
                });
                const data = await response.json();
                if (!cancelled && data?.joined) setState("joined");
            } catch (error) {
                // Une demande en double est sans effet : on laisse le bouton.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [session?.user, status]);

    const join = async (event) => {
        event.preventDefault();
        if (state === "loading") return;
        if (!session?.user && !email.trim()) return;
        setState("loading");
        try {
            const response = await fetch("/api/course-waitlist/join", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    courseSlug: CPF_SLUG,
                    courseName: "Accélérateur Python (financement CPF)",
                    contexte: "cpf",
                    email: session?.user ? null : email.trim(),
                }),
            });
            if (!response.ok) throw new Error("refus");
            try {
                window.localStorage.setItem(
                    CPF_STORAGE,
                    JSON.stringify({ at: new Date().toISOString() }),
                );
            } catch (error) {
                // L'inscription est en base, seul le rappel visuel est perdu.
            }
            setState("joined");
        } catch (error) {
            setState("error");
        }
    };

    return (
        <section id="cpf" className="scroll-mt-24 bg-white px-6">
            <FadeIn className="mx-auto max-w-6xl">
                <div
                    className="relative grid overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFF6D8] via-[#FFF9EC] to-[#F1ECFF] ring-1 ring-slate-900/5 lg:grid-cols-[1.1fr_1fr]"
                >
                    {/* BeBot attend le CPF sur son banc : on sera prévenu. */}
                    <div className="relative order-1 h-56 sm:h-72 lg:order-2 lg:h-auto">
                        <Image
                            src={IMG("cpf.jpg")}
                            alt=""
                            fill
                            sizes="(min-width: 1024px) 540px, 100vw"
                            className="object-cover"
                        />
                    </div>

                    <div className="relative order-2 p-7 sm:p-12 lg:order-1 lg:py-14">
                        <Eyebrow>{t("cpf_eyebrow")}</Eyebrow>
                        <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl sm:leading-[1.1]">
                            {t("cpf_title")}
                        </h2>
                        <p className="mb-0 mt-4 max-w-lg text-[15px] leading-relaxed text-slate-600">
                            {t("cpf_text")}
                        </p>

                        <div className="mt-8 max-w-lg">
                            <AnimatePresence mode="wait" initial={false}>
                                {state === "joined" ? (
                                    <motion.div
                                        key="joined"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.25, ease: EASE }}
                                        className="flex items-start gap-3 rounded-2xl bg-white/80 p-5 ring-1 ring-slate-900/5"
                                    >
                                        <SolarIcon name="check-circle-bold" className="mt-0.5 shrink-0 text-xl text-primary" />
                                        <p className="mb-0 text-[15px] leading-relaxed text-slate-800">
                                            {t("cpf_joined")}
                                        </p>
                                    </motion.div>
                                ) : (
                                    <motion.form
                                        key="form"
                                        onSubmit={join}
                                        initial={false}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.2, ease: EASE }}
                                    >
                                        {session?.user ? (
                                            <Button type="primary" isLoading={state === "loading"} progress className="!rounded-full">
                                                {t("cpf_button_member")}
                                            </Button>
                                        ) : (
                                            <>
                                                <label htmlFor="cpf-email" className="sr-only">
                                                    {t("cpf_email_label")}
                                                </label>
                                                <div className="flex flex-col gap-3 rounded-full sm:flex-row sm:bg-white sm:p-1.5 sm:shadow-[0_12px_30px_-16px_rgba(76,52,170,0.45)] sm:ring-1 sm:ring-slate-900/5">
                                                    <input
                                                        id="cpf-email"
                                                        type="email"
                                                        required
                                                        autoComplete="email"
                                                        value={email}
                                                        onChange={(event) => setEmail(event.target.value)}
                                                        placeholder={t("cpf_placeholder")}
                                                        className="min-w-0 flex-1 rounded-full border border-slate-200 bg-white px-5 py-3 text-[15px] text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-primary sm:border-0 sm:bg-transparent"
                                                    />
                                                    <Button type="primary" isLoading={state === "loading"} progress className="!rounded-full">
                                                        {t("cpf_button")}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                        {state === "error" && (
                                            <p className="mb-0 mt-3 text-sm text-red-600">{t("cpf_error")}</p>
                                        )}
                                        <p className="mb-0 mt-3 flex items-center gap-2 text-xs text-slate-500">
                                            <SolarIcon name="shield-check-bold-duotone" className="text-sm text-[#613BDB]" />
                                            {t("cpf_privacy")}
                                        </p>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </FadeIn>
        </section>
    );
}

function Faq({ t }) {
    const [open, setOpen] = useState(-1);
    return (
        <section id="faq" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1fr_1.6fr]">
                <FadeIn>
                    <Eyebrow>{t("faq_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("faq_title")}
                    </h2>
                </FadeIn>
                <div className="divide-y divide-slate-200 border-y border-slate-200">
                    {FAQ.map((question, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={question}>
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    aria-expanded={isOpen}
                                    className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left"
                                >
                                    <span className="font-semibold text-slate-900">
                                        {t(`faq_${question}_q`)}
                                    </span>
                                    <SolarIcon
                                        name="alt-arrow-down-linear"
                                        className={`shrink-0 text-xl text-slate-400 transition-transform duration-300 ${
                                            isOpen ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: EASE }}
                                            className="overflow-hidden"
                                        >
                                            <p className="mb-0 pb-5 pr-10 text-[15px] leading-relaxed text-slate-600">
                                                {t(`faq_${question}_a`)}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// Le dernier panneau : BeBot saute de joie avec le python, entouré de la
// semaine qui démarre. Les objets suivent la souris comme dans le hero.
const FINAL_ITEMS = [
    { src: "calendrier", x: 4, y: 8, w: 22, depth: 1.3, rot: -10, float: 4.8 },
    { src: "avion", x: 72, y: 2, w: 24, depth: 1.7, rot: 6, float: 4 },
    { src: "enveloppe", x: 78, y: 52, w: 20, depth: 1.1, rot: 10, float: 5.4 },
    { src: "code", x: 0, y: 62, w: 22, depth: 0.9, rot: -5, float: 5.8 },
];

function FinalCta({ t, onApply }) {
    const reduce = useReducedMotion();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const bebot = useParallax(mx, my, 0.4);
    const onMove = (event) => {
        if (reduce || event.pointerType !== "mouse") return;
        const rect = event.currentTarget.getBoundingClientRect();
        mx.set((event.clientX - rect.left) / rect.width - 0.5);
        my.set((event.clientY - rect.top) / rect.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section className="bg-white px-6 pb-20 pt-4 sm:pb-28">
            <div
                className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-[#F8F6FF] ring-1 ring-slate-900/5"
                onPointerMove={onMove}
                onPointerLeave={onLeave}
            >
                <div aria-hidden className="pointer-events-none absolute inset-0">
                    <div className="absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-[#E4DCFF] blur-3xl" />
                    <div className="absolute -right-20 top-0 h-[360px] w-[360px] rounded-full bg-[#D8ECFF] blur-3xl" />
                    <div className="absolute -bottom-40 left-1/3 h-[360px] w-[360px] rounded-full bg-[#FFF1C9] blur-3xl" />
                </div>

                <div className="relative grid items-center gap-4 px-7 py-12 sm:px-12 lg:grid-cols-[1.1fr_1fr] lg:py-14">
                    <FadeIn className="order-2 text-center lg:order-1 lg:text-left">
                        <h2 className="font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl sm:leading-[1.05]">
                            {t("bottom_cta_title")}
                        </h2>
                        <p className="mx-auto mb-0 mt-5 max-w-xl text-lg text-slate-600 lg:mx-0">
                            {t("bottom_cta_subtitle")}
                        </p>
                        <div className="mt-9 flex flex-col items-center gap-6 lg:items-start">
                            <Price t={t} />
                            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-center lg:justify-start">
                                <Button type="primary" onClick={onApply} className="w-full !rounded-full text-base font-semibold sm:w-auto">
                                    <span className="inline-flex items-center gap-2">
                                        {t("bottom_cta_button")}
                                        <SolarIcon name="arrow-right-linear" className="text-lg" />
                                    </span>
                                </Button>
                                <DownloadLink t={t} className="bg-white/70 backdrop-blur" />
                            </div>
                            <p className="mb-0 text-xs text-slate-500">{t("footer_note")}</p>
                        </div>
                    </FadeIn>

                    <div className="order-1 lg:order-2">
                        <div className="relative mx-auto aspect-[1/1] w-full max-w-[420px]">
                            <div
                                aria-hidden
                                className="absolute inset-[10%] rounded-[46%_54%_42%_58%/50%_46%_54%_50%] bg-gradient-to-br from-[#DCE9FF] via-[#E6DEFF] to-[#DDF6EA]"
                            />
                            <motion.div
                                className="absolute left-[19%] top-[10%] w-[62%]"
                                style={{ x: bebot.x, y: bebot.y }}
                            >
                                <motion.div
                                    animate={reduce ? undefined : { y: [0, -14, 0] }}
                                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Image
                                        src={IMG("hero/bebot-joie.webp")}
                                        alt=""
                                        width={800}
                                        height={915}
                                        sizes="(min-width: 1024px) 260px, 60vw"
                                        className="h-auto w-full select-none"
                                        draggable={false}
                                    />
                                </motion.div>
                            </motion.div>
                            {FINAL_ITEMS.map((item, i) => (
                                <HeroItem key={item.src} item={item} index={i} mx={mx} my={my} reduce={reduce} />
                            ))}
                            <HeroChip
                                chip={{ key: "bottom_cta_chip", x: 30, y: 88, depth: 1.4, delay: 0.4 }}
                                t={t}
                                mx={mx}
                                my={my}
                                reduce={reduce}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Le sommaire collant, dans l'ordre de la page.
const SECTION_NAV = ["en-bref", "programme", "projets", "suivi", "certification", "resultats", "faq"];

export default function PythonLanding() {
    const { data: session } = useSession();
    const [showLeadModal, setShowLeadModal] = useState(false);
    const t = useTranslations("PythonAcceleratorLanding");
    const tNav = useTranslations();

    const openModal = () => setShowLeadModal(true);

    return (
        <div className="relative min-h-screen bg-white text-slate-900">
            {!session && (
                <header className="sticky top-0 z-50 border-b border-slate-900/5 bg-[#F8F6FF]/80 backdrop-blur-xl">
                    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
                        <Link href="/" className="flex items-center">
                            <Logo />
                        </Link>
                        <div className="flex items-center gap-6">
                            <a
                                href="#programme"
                                className="hidden text-sm text-slate-500 transition-colors hover:text-slate-900 sm:inline-flex"
                            >
                                {t("nav_programme")}
                            </a>
                            <Link
                                href="/"
                                className="hidden text-sm text-slate-500 transition-colors hover:text-slate-900 sm:inline-flex"
                            >
                                {tNav("back_to_home")}
                            </Link>
                            <button
                                type="button"
                                onClick={openModal}
                                className="cursor-pointer rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                            >
                                {t("cta_primary")}
                            </button>
                        </div>
                    </div>
                </header>
            )}

            <main className="relative">
                <Hero t={t} onApply={openModal} />
                <AcceleratorSectionNav
                    items={SECTION_NAV.map((id) => ({ id, label: t(`section_nav_${id}`) }))}
                    label={t("section_nav_label")}
                    ctaLabel={t("cta_primary")}
                    onCta={openModal}
                    loggedIn={Boolean(session)}
                />
                <Brief t={t} />
                <Uses t={t} />
                <Programme t={t} onApply={openModal} />
                <Projects t={t} />
                <Timeline t={t} />
                <FollowUp t={t} />
                <Trainer t={t} />
                <Certification t={t} />
                <Stats t={t} />

                {/* Informations obligatoires avant inscription */}
                <TrainingFactSheet
                    program="python"
                    values={trainingFactValues("python")}
                    light
                />

                <CpfWaitlist t={t} />
                <Faq t={t} />
                <FinalCta t={t} onApply={openModal} />
            </main>

            <AcceleratorLeadModal
                open={showLeadModal}
                onOpenChange={setShowLeadModal}
                accelerator="python"
                calLink="https://cal.eu/believemy/accelerateur"
            />
        </div>
    );
}
